import { useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { useAuth } from "./use-auth";

declare global {
  interface Window {
    rdt?: (...args: unknown[]) => void;
  }
}

async function sha256Hex(value: string): Promise<string> {
  const buffer = new TextEncoder().encode(value.trim().toLowerCase());
  const hashBuffer = await crypto.subtle.digest("SHA-256", buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

const PIXEL_ID = import.meta.env.VITE_REDDIT_PIXEL_ID as string | undefined;

function getRdtCid(): string | undefined {
  try {
    return localStorage.getItem("rdt_cid") ?? undefined;
  } catch {
    return undefined;
  }
}

export function useRedditPixel() {
  const { user, loading } = useAuth();
  const [location] = useLocation();
  const prevLocation = useRef<string | null>(null);
  const prevUserId = useRef<number | null | undefined>(undefined);

  useEffect(() => {
    if (loading) return;
    if (!PIXEL_ID || !window.rdt) return;

    const isFirstResolve = prevUserId.current === undefined;
    const userChanged = prevUserId.current !== (user?.id ?? null);
    prevUserId.current = user?.id ?? null;

    if (!isFirstResolve && !userChanged) return;

    const rdtCid = getRdtCid();

    async function reinit() {
      const opts: Record<string, string> = {};
      if (rdtCid) opts.clickId = rdtCid;
      if (user) {
        opts.externalId = String(user.id);
        try {
          opts.email = await sha256Hex(user.email);
        } catch {
          // SubtleCrypto unavailable in non-secure contexts — skip email
        }
      }
      window.rdt?.(
        "init",
        PIXEL_ID,
        Object.keys(opts).length > 0 ? opts : undefined,
      );
      const pageVisitData = rdtCid ? { clickId: rdtCid } : undefined;
      window.rdt?.("track", "PageVisit", pageVisitData);
    }

    reinit();
  }, [loading, user]);

  useEffect(() => {
    if (prevLocation.current === null) {
      prevLocation.current = location;
      return;
    }
    if (location === prevLocation.current) return;
    prevLocation.current = location;

    if (!PIXEL_ID || !window.rdt) return;

    const rdtCid = getRdtCid();
    const eventData = rdtCid ? { clickId: rdtCid } : undefined;
    window.rdt?.("track", "PageVisit", eventData);
  }, [location]);
}
